import TemplateSurveyState from 'containers/SurveyPage/TemplateSurveyState';
import { find, get, flow, first, cloneDeep } from 'lodash';

import { mapQuestionsAndGroupsAsNodes } from 'containers/QuestionnairePage/utils';
import { transformPropsDeep, escapeHTML } from 'containers/App/utils';
import { Matcher, Operator } from 'utils/adl-services';
import {
  SURVEY_TYPE_PEEQ,
  SURVEY_TYPE_DISTRIBUTION_LINK,
} from 'containers/Survey/constants';

import {
  getEmptyAnswer as getEmptyScreeningAnswer,
  getEmptyNoneOfTheAboveAnswer,
} from 'components/ScreeningQuestion/utils';
import {
  getEmptyAnswer,
  noneOfTheAboveAnswerSelector,
} from 'containers/QuestionnairePage/GenericQuestion/SingleMultipleQuestion/utils';

import { END_OF_SURVEY } from 'components/LogicPathPreview/constants';
import { FAILURE, REASON_SCREENOUT } from 'components/LogicOutcome/constants';
import { SCREENING_ALLOWED_VALUES } from 'components/ScreeningQuestion/constants';
import {
  screeningQuestionToNormal,
  addScreeningQuestionWithScreeningQL,
  repointCarryForwardFromScreening,
  shouldUseScreeningQl,
  shouldConvertScreeningToQlQuestions,
  applyScreeningQuestionContentUpdate,
} from '../utils';

const cpgBrandAwarenessPerceptionsGenerator = ({
  template,
  variables,
  surveyType,
  peeqCategoryType,
}) => {
  // pass the fetchedXXXX options only
  // if you intend to use the relevant template as-is

  // extract and prepare user variables
  const productCategory = get(
    find(variables, ['name', 'product_category']),
    'value',
    {},
  );
  const yourBrand = get(find(variables, ['name', 'your_brand']), 'value', {});
  const competitorBrands = get(
    find(variables, ['name', 'competitor_brands']),
    'value',
    [],
  );

  const storeBrands = get(
    find(variables, ['name', 'store_brand']),
    'value',
    [],
  );
  const otherProductCategories = get(
    find(variables, ['name', 'other_product_categories']),
    'value',
    [],
  );
  const features = get(find(variables, ['name', 'features']), 'value', []);
  const statements = get(find(variables, ['name', 'statements']), 'value', []);

  const period = get(find(variables, ['name', 'period']), 'value', '');
  const includeFast = get(
    find(variables, ['name', 'include_fast']),
    'value',
    false,
  );

  // replace general texts
  const replacePeriod = (value) =>
    value.replace(/\{\{period\}\}/gi, escapeHTML(period));
  const replaceProductCategory = (value) =>
    value.replace(
      /\{\{product_category\}\}/gi,
      escapeHTML(productCategory.text),
    );
  const replaceYourBrand = (value) =>
    value.replace(/\{\{your_channel\}\}/gi, escapeHTML(yourBrand.text));

  const replaceAllTexts = flow([
    replaceYourBrand,
    replaceProductCategory,
    replacePeriod,
  ]);

  const questionnaireTemplate = transformPropsDeep(
    template.questionnaireTemplate,
    {
      prompt: replaceAllTexts,
      htmlPrompt: replaceAllTexts,
      body: replaceAllTexts,
    },
  );

  const {
    nodes: cpgBrandAwarenessPerceptionsTemplateQuestions,
    screeningQuestions,
  } = mapQuestionsAndGroupsAsNodes(questionnaireTemplate);

  const screeningQuestionClone = cloneDeep(screeningQuestions[0]);
  Object.assign(screeningQuestionClone, {
    answers: [
      {
        ...getEmptyScreeningAnswer(),
        body: productCategory.text,
        media: productCategory.media,
      },
      ...otherProductCategories.map((otherCategory) => ({
        ...getEmptyScreeningAnswer(),
        body: otherCategory.text,
        media: otherCategory.media,
        screenerType: SCREENING_ALLOWED_VALUES.NOT_ALLOWED,
      })),
      {
        ...getEmptyNoneOfTheAboveAnswer(),
        screenerType: SCREENING_ALLOWED_VALUES.STRICT_NOT_ALLOWED,
      },
    ],
  });

  const Survey = new TemplateSurveyState({
    fetchedQuestionnaire: {
      ...questionnaireTemplate,
      isPeeq: surveyType === SURVEY_TYPE_PEEQ,
      isDistributionLink: surveyType === SURVEY_TYPE_DISTRIBUTION_LINK,
    },
    fetchedTargeting:
      surveyType === SURVEY_TYPE_PEEQ
        ? template.peeqTargetingTemplate
        : template.regularTargetingTemplate,
  });

  // setup media for questions
  [0, 1, 2, 3, 6, 21].forEach((qIndex) => {
    Survey.questionnaire.updateQuestion({
      ...cpgBrandAwarenessPerceptionsTemplateQuestions[qIndex],
      media: productCategory.media,
    });
  });

  // eslint-disable-next-line no-underscore-dangle
  const CFSourceQuestion = Object.assign(
    {},
    // eslint-disable-next-line no-underscore-dangle
    Survey.questionnaire._getQuestion(
      cpgBrandAwarenessPerceptionsTemplateQuestions[3].refId,
    ),
    {
      answers: [
        {
          ...getEmptyAnswer(true),
          body: yourBrand.text,
          media: yourBrand.media,
        },
        ...competitorBrands.map((competitorBrand) => ({
          ...getEmptyAnswer(true),
          body: competitorBrand.text,
          media: competitorBrand.media,
        })),
        ...storeBrands.map((storeBrand) => ({
          ...getEmptyAnswer(true),
          body: storeBrand.text,
          media: storeBrand.media,
        })),
        noneOfTheAboveAnswerSelector(
          cpgBrandAwarenessPerceptionsTemplateQuestions[3].answers,
        ),
      ],
    },
  );

  Survey.questionnaire.updateQuestion(CFSourceQuestion);

  Survey.questionnaire.updateQuestion({
    ...cpgBrandAwarenessPerceptionsTemplateQuestions[5],
    answers: statements.map((statement) => ({
      ...getEmptyAnswer(true),
      body: statement.text,
    })),
  });

  Survey.questionnaire.updateQuestion({
    ...cpgBrandAwarenessPerceptionsTemplateQuestions[22],
    answers: statements.map((statement) => ({
      ...getEmptyAnswer(true),
      body: statement.text,
    })),
  });

  Survey.questionnaire.updateQuestion({
    ...cpgBrandAwarenessPerceptionsTemplateQuestions[26],
    answers: features.map((feature) => ({
      ...getEmptyAnswer(true),
      body: feature,
    })),
  });

  // second pass for CF
  Survey.targeting.updateScreeningQuestion(screeningQuestionClone);
  applyScreeningQuestionContentUpdate(Survey, screeningQuestionClone, {
    surveyType,
    peeqCategoryType,
  });

  Survey.questionnaire.updateQuestion(CFSourceQuestion);

  if (includeFast) {
    Survey.questionnaire.removeQuestion({
      refId: cpgBrandAwarenessPerceptionsTemplateQuestions[28].refId,
    });
    Survey.questionnaire.removeQuestion({
      refId: cpgBrandAwarenessPerceptionsTemplateQuestions[1].refId,
    });
  } else {
    Survey.questionnaire.removeQuestion({
      refId: cpgBrandAwarenessPerceptionsTemplateQuestions[27].refId,
    });
    Survey.questionnaire.removeQuestion({
      refId: cpgBrandAwarenessPerceptionsTemplateQuestions[0].refId,
    });
  }

  // if creating a distribution link or PEEQ survey
  // we need to handle the screening questions
  // and convert them to normal questions
  // with a screen out reason in the ADL
  const useNewScreening = shouldUseScreeningQl({
    surveyType,
    peeqCategoryType,
  });
  const convertScreeningToAdlQuestions = shouldConvertScreeningToQlQuestions({
    surveyType,
    peeqCategoryType,
  });

  if (convertScreeningToAdlQuestions) {
    // add questions
    // remove from question:isScreening
    // and from answers: screenerType, screening,
    const screenerQuestion = screeningQuestionToNormal(screeningQuestionClone);
    Survey.questionnaire.addQuestion(screenerQuestion);

    repointCarryForwardFromScreening(
      Survey,
      screenerQuestion.refId,
      screeningQuestionClone.refId,
    );

    // move to top of questionnaire
    Survey.questionnaire.moveQuestion(
      screenerQuestion,
      // eslint-disable-next-line no-underscore-dangle
      -(Survey.questionnaire._getQuestionCount() - 1),
    );

    Survey.questionnaire.removeAllScreeningQuestions();

    // set ADL to screenout
    const firstStatementId = Survey.questionnaire.linkAnswerToQuestion({
      question: screenerQuestion,
      answerRefId: first(screenerQuestion.answers).refId,
      targetQuestion: { refId: END_OF_SURVEY },
      context: { questionId: screenerQuestion.refId },
      matcher: Matcher.NotEqual,
    });
    Survey.questionnaire.updateStatementOutcome({
      nodeRefId: screenerQuestion.refId,
      statementId: firstStatementId,
      outcome: {
        params: { id: END_OF_SURVEY },
        type: FAILURE,
        reason: REASON_SCREENOUT,
      },
    });
  } else if (useNewScreening) {
    // add questions
    // remove from question:isScreening
    // and from answers: screenerType, screening,
    const newQLScreener = addScreeningQuestionWithScreeningQL(
      Survey,
      screeningQuestionClone,
    );
    applyScreeningQuestionContentUpdate(Survey, newQLScreener, {
      surveyType,
      peeqCategoryType,
    });

    repointCarryForwardFromScreening(
      Survey,
      newQLScreener.refId,
      screeningQuestionClone.refId,
    );

    Survey.questionnaire.removeQuestion(screeningQuestionClone);
    Survey.targeting.removeAllScreeningQuestion();
  }

  // setup logic for q12 and q29 (ADL)
  // make sure all answers selected match the one from th CF in q4
  // eslint-disable-next-line no-underscore-dangle
  const sourceQuestion = Survey.questionnaire._getQuestion(
    cpgBrandAwarenessPerceptionsTemplateQuestions[4].refId,
  );

  [12, 30].forEach((qIndex) => {
    // eslint-disable-next-line no-underscore-dangle
    const activeQuestion = Survey.questionnaire._getQuestion(
      cpgBrandAwarenessPerceptionsTemplateQuestions[qIndex].refId,
    );
    activeQuestion.answers.forEach((answer) => {
      // handle statement/rule by matching answers with q4 answers

      // current answer
      const statementId = Survey.questionnaire.linkAnswerToQuestion({
        question: activeQuestion,
        answerRefId: answer.refId,
        targetQuestion: { refId: END_OF_SURVEY },
        context: { questionId: activeQuestion.refId },
        Matcher: Matcher.NotEqual,
      });
      if (convertScreeningToAdlQuestions) {
        Survey.questionnaire.updateStatementOutcome({
          nodeRefId: activeQuestion.refId,
          statementId,
          outcome: {
            params: { id: END_OF_SURVEY },
            type: FAILURE,
            reason: REASON_SCREENOUT,
          },
        });
      }
      // source answer
      // statementId created in previous step
      // nodeRefId is the active question
      // matcher is equal by default
      // also need context of source question
      // and answer of source question
      Survey.questionnaire.addRuleToStatement({
        question: activeQuestion,
        statementId,
        matcher: Matcher.NotEqual,
        answer: sourceQuestion.answers.find(
          (sourceAnswer) =>
            sourceAnswer.originalAnswerRefId === answer.originalAnswerRefId,
        ),
        context: {
          questionId: sourceQuestion.refId,
        },
      });

      Survey.questionnaire.updateRuleOperatorOfStatement(
        activeQuestion,
        statementId,
        Operator.All,
      );
    });
  });

  return { state: Survey.state };
};

export default cpgBrandAwarenessPerceptionsGenerator;
